import { DraggableAttributes, useDndMonitor } from "@dnd-kit/core";
import { SyntheticListenerMap } from "@dnd-kit/core/dist/hooks/utilities";
import { arrayMove, useSortable } from "@dnd-kit/sortable";
import { useMemo, useRef } from "react";
import { useDndSortZoneContext } from "./DndSortZoneContext";
import { useDndSortContext } from "./DndSortContext";
import {
  calculateZoneValidity,
  resolveDropZone,
  WINDOW_PROPERTY_SORT_POSITION,
} from "./utils";
import { DndSortChangeEvent } from "../types";
import { DndSortLine } from "../../Dnd/DndSortLine";

type DndSortItemChildrenParams = {
  setNodeRef: (node: HTMLElement | null) => void;
  setActivatorNodeRef: (node: HTMLElement | null) => void;
  attributes: DraggableAttributes;
  listeners?: SyntheticListenerMap;
  style: React.CSSProperties;
};

type DndSortItemProps = {
  children: (params: DndSortItemChildrenParams) => React.ReactNode;
  id: string | number;
  label: string;
  onDrop?: (event: DndSortChangeEvent) => void;
  type?: string;
};

/**
 * A generic draggable component for the DndSort system. Can be used to create any draggable item.
 *
 * @param props
 * @param props.children A function that receives the necessary props to make the item draggable
 * @param props.id The unique identifier for the draggable item
 * @param props.label The label for the draggable item
 * @param props.onDrop The callback function to be called when the item is dropped
 * @param props.type The type of the draggable item, used for validation (optional)
 * @returns
 */
export default function DndSortItem({
  children: childrenFn,
  id,
  label,
  onDrop: onDropProp,
  type,
}: DndSortItemProps) {
  const { onDrop } = useDndSortContext();
  const zone = useDndSortZoneContext();
  const zoneId = zone?.id || "";
  const sortable = zone?.sortable || false;
  const orientation = zone?.orientation || "vertical";
  const defaultDropPosition = zone?.defaultDropPosition || "end";

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transition,
    active,
    items,
    activeIndex,
    newIndex,
    index,
    isOver,
    over,
  } = useSortable({
    id,
    data: {
      label,
      type,
      zone,
    },
  });

  useDndMonitor({
    onDragStart() {
      // Clear out the sort position so that we don't have stale data from the last interaction
      window[WINDOW_PROPERTY_SORT_POSITION] = undefined;
    },
    onDragEnd(event) {
      if (active?.id !== id) {
        return;
      }

      const newZone = resolveDropZone(event);
      const newZoneItems: (string | number)[] =
        newZone?.data.current?.sortedIds || [];
      const newZoneDefaultDrop = newZone?.data.current?.defaultDropPosition;

      let zoneSort = null;

      if (newZone && newZone.data.current?.sortable) {
        // The target zone is sortable
        if (newZone.id === zoneId) {
          zoneSort = arrayMove(newZoneItems, activeIndex, newIndex); // move within the same zone
        } else if (!event.over?.data.current?.isZone) {
          const droppedOnIndex = event.over?.data.current?.sortable.index;
          // dropped on another item
          if (newZoneDefaultDrop === "start") {
            // place the dropped item after the item
            zoneSort = newZoneItems.toSpliced(droppedOnIndex + 1, 0, id);
          } else {
            // place the dropped item before the item
            zoneSort = newZoneItems.toSpliced(droppedOnIndex, 0, id);
          }
        } else if (newZoneDefaultDrop === "start") {
          zoneSort = [id].concat(newZoneItems); // add to the start
        } else {
          zoneSort = newZoneItems.concat(id); // add to the end
        }
      }

      const dropEvent = {
        draggableId: event.active.id,
        zoneId: newZone?.id || null,
        previousZoneId: event.active.data.current?.zone?.id || null,
        valid: calculateZoneValidity({ active: event.active, zone: newZone }),
        previousZoneSort: sortable
          ? newZone?.id === zoneId
            ? zoneSort
            : items.filter((i) => i !== id)
          : null,
        zoneSort,
      };
      onDrop(dropEvent); // Call for the context level
      onDropProp?.(dropEvent); // Call for the leaf level
    },
  });

  const internalRef = useRef<HTMLElement | null>();

  const style = {
    transition,
  };

  const refCallback = (node: HTMLElement | null) => {
    internalRef.current = node;
    setNodeRef(node);
  };

  const children = childrenFn({
    setNodeRef: refCallback,
    setActivatorNodeRef,
    attributes,
    listeners,
    style,
  });

  const line = useMemo(() => {
    const isActiveInSameZone = active?.data.current?.zone?.id === zoneId;

    const isFirst = index === 0;
    const isLast = items.length - 1 === index;

    // Item dragged is in the same zone as this item
    const isSortingWithinZone =
      sortable && isOver && index !== activeIndex && isActiveInSameZone;

    // Item dragged is coming from another zone (or no zone at all) and is being dropped on an item in this zone
    const isSortingFromOutsideZone = sortable && isOver && !isActiveInSameZone;

    // Item dragged is coming from another zone (or no zone at all) and is being dropped on the zone itself
    // And this item is either the first or last item in the zone, respective of the default drop position
    const isSortingDefault =
      sortable &&
      over?.data.current?.isZone === true &&
      !isActiveInSameZone &&
      ((isFirst && defaultDropPosition === "start") ||
        (isLast && defaultDropPosition === "end"));
    const show =
      isSortingWithinZone || isSortingFromOutsideZone || isSortingDefault;
    let position: "after" | "before" = "after";
    if (isSortingWithinZone) {
      position = newIndex > index ? "before" : "after";
    } else if (isSortingFromOutsideZone) {
      position = defaultDropPosition === "start" ? "after" : "before";
    } else if (isSortingDefault) {
      position = defaultDropPosition === "start" ? "before" : "after";
    }
    return { show, position };
  }, [
    active?.data,
    activeIndex,
    defaultDropPosition,
    index,
    isOver,
    items.length,
    newIndex,
    over?.data,
    sortable,
    zoneId,
  ]);

  return (
    <div
      style={{
        position: "relative",
      }}
    >
      {line.show && (
        <DndSortLine
          offset={`calc(-1 * var(--drop-zone-gap) / 2)`}
          orientation={orientation}
          position={line.position}
        />
      )}
      {children}
    </div>
  );
}
